import { AxiosResponse } from 'axios';
import { FBaseService } from '@kasasa/fbase-components/lib';
import { Cycle } from './Cycle';
import { CycleSet } from './CycleSet';

export class CycleResource extends FBaseService {
  public async getCycle(fiId: string, cycleId: number): Promise<Cycle> {
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/cycle/${cycleId}`,
    );

    const result: Cycle = response.data.data;
    return result;
  }

  public async getAllCycles(fiId: string): Promise<Cycle[]> {
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/cycle-set`,
    );

    const result: CycleSet[] = response.data.data;

    const cycles: Cycle[] = [];
    result.forEach((set: CycleSet) => {
      set.cyclesDesc.forEach((cycle: Cycle) => {
        cycles.push(cycle);
      });
    });
    return cycles;
  }

  public async getAllCycleSets(fiId: string): Promise<CycleSet[]> {
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/cycle-set`,
    );
    return response.data.data;
  }

  public async removeCycleSet(fiId: string, cycleSetId: number): Promise<AxiosResponse> {
    return this.apiClient.delete(`/api/rcx/config/${fiId}/cycle-set/${cycleSetId}`);
  }

  public async removeCycle(fiId: string, cycleId: number): Promise<AxiosResponse> {
    return this.apiClient.delete(`/api/rcx/config/${fiId}/cycle/${cycleId}`);
  }

  public async saveCycleSet(fiId: string, cycleSet: CycleSet): Promise<CycleSet> {
    const url = `/api/rcx/config/${fiId}/cycle-set`;
    let response: AxiosResponse;

    const toSave: any = {};
    Object.assign(toSave, cycleSet);

    if (cycleSet.id) {
      response = await this.apiClient.put(`${url}/${cycleSet.id}`, toSave);
    } else {
      response = await this.apiClient.post(url, toSave);
    }

    const savedCycleSet: CycleSet = response.data.data;
    return savedCycleSet;
  }

  public async saveCycle(fiId: string, cycle: Cycle): Promise<Cycle> {
    const url = `/api/rcx/config/${fiId}/cycle`;
    let response: AxiosResponse;

    const toSave: any = {};
    Object.assign(toSave, cycle);
    try {
      if (cycle.id) {
        response = await this.apiClient.put(`${url}/${cycle.id}`, toSave);
      } else {
        response = await this.apiClient.post(url, toSave);
      }
      const savedCycle: Cycle = response.data.data;
      return savedCycle;
    } catch (error) {
      throw error;
    }
  }

  public async getAllCyclesWithMonthEndDate(fiId: string): Promise<Cycle[]> {
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/cycle-set`,
    );

    const result: CycleSet[] = response.data.data;

    const cycles: Cycle[] = [];
    result.forEach((set: CycleSet) => {
      set.cyclesDesc.forEach((cycle: Cycle) => {
        if (!CycleResource.isStatementEndDateEndOfMonth(new Date(cycle.statementEndDate))) {
          const endOfMonthCycle: Cycle = {} as Cycle;
          const monthEndDate = CycleResource.getMonthEndDateFromStatementEnd(new Date(cycle.statementEndDate));
          endOfMonthCycle.endOfMonthDateInCycle = monthEndDate;

          cycles.push(endOfMonthCycle);
        }
        cycles.push(cycle);
      });
    });
    return cycles;
  }

  private static getMonthEndDateFromStatementEnd(statementEndDate: Date): string {
    // Sets the date to the zeroeth day of the next month, which Javascript interprets as the last day of this month
    const endOfMonthDate: Date = new Date(statementEndDate.getFullYear(), statementEndDate.getMonth() + 1, 0);

    return CycleResource.getDateStringForRequest(endOfMonthDate);
  }

  private static isStatementEndDateEndOfMonth(statementEndDate: Date): boolean {
    statementEndDate.setDate(statementEndDate.getDate() + 1);
    return statementEndDate.getUTCDate() === 1;
  }

  public async getActiveCycles(fiId: string): Promise<Cycle[]> {
    const today = CycleResource.getTodaysDate();
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/cycle/date/${today}`,
    );

    const result: Cycle[] = response.data.data;
    return result;
  }

  private static getTodaysDate(): string {
    const today = new Date();
    return CycleResource.getDateStringForRequest(today);
  }

  private static getDateStringForRequest(dateForRequest: Date): string {
    return dateForRequest.toISOString().slice(0, 10);
  }
}

export default CycleResource;
